import { type InputHTMLAttributes, type ReactElement, type ReactNode } from 'react';
import { cn } from '../../lib/utils.js';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  readonly label?: ReactNode;
  readonly variant?: 'default' | 'chip';
}

export function Checkbox({
  id,
  checked = false,
  disabled = false,
  label,
  variant = 'default',
  className,
  ...props
}: CheckboxProps): ReactElement {
  return (
    <label
      htmlFor={id}
      className={cn(
        'group inline-flex select-none items-center gap-2 text-[12px] leading-none align-middle transition-all',
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
        variant === 'chip' &&
          cn(
            'box-border h-8 rounded-control border px-2.5 py-0 font-medium leading-none',
            checked
              ? 'border-brand-border-tint bg-brand-wash text-fg'
              : 'border-border bg-surface-subtle text-fg-secondary hover:border-border-medium hover:text-fg',
          ),
        variant === 'default' && 'font-medium text-fg-secondary hover:text-fg',
        className,
      )}
    >
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        className="peer sr-only"
        {...props}
      />
      <span
        aria-hidden="true"
        className={cn(
          'flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[5px] border transition-all duration-150 peer-focus-visible:ring-2 peer-focus-visible:ring-brand-wash',
          checked
            ? 'border-brand bg-brand text-brand-fg shadow-primary-glow'
            : 'border-border bg-surface-subtle group-hover:border-brand',
        )}
      >
        <svg
          width="11"
          height="11"
          viewBox="0 0 12 12"
          fill="none"
          aria-hidden="true"
          className={cn(
            'transition-all duration-150',
            checked ? 'scale-100 opacity-100' : 'scale-75 opacity-0',
          )}
        >
          <path
            d="M2.25 6.25L4.75 8.75L9.75 3.25"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      {label !== undefined && <span>{label}</span>}
    </label>
  );
}

export interface SwitchProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  readonly label?: ReactNode;
}

export function Switch({
  id,
  checked = false,
  disabled = false,
  label,
  className,
  ...props
}: SwitchProps): ReactElement {
  return (
    <label
      htmlFor={id}
      className={cn(
        'group inline-flex select-none items-center gap-2.5 text-[12px] font-medium text-fg-secondary transition-colors hover:text-fg',
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
        className,
      )}
    >
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        className="peer sr-only"
        {...props}
      />
      <span
        aria-hidden="true"
        className={cn(
          'relative inline-flex h-[22px] w-[40px] shrink-0 items-center rounded-pill border p-0.5 transition-colors duration-200 peer-focus-visible:ring-2 peer-focus-visible:ring-brand-wash',
          checked
            ? 'border-brand bg-brand'
            : 'border-border bg-surface-muted group-hover:border-border-medium',
        )}
      >
        <span
          className={cn(
            'h-4 w-4 rounded-pill bg-surface shadow-sm transition-transform duration-200',
            checked ? 'translate-x-[18px] bg-brand-fg' : 'translate-x-0',
          )}
        />
      </span>
      {label !== undefined && <span>{label}</span>}
    </label>
  );
}
